import {
  DrySuiteEngine,
  ObservedProperty,
  DSCustomElement,
  Dict,
  DSElementSetupHooks,
  DSElementFactory,
  DSElementConstructor,
  EngineStreams,
} from './core-types';
import { enableProps, setPropFromAttr } from './element-feature-adaptors/props';
import { setupShadowDOM } from './element-feature-adaptors/shadow-dom';
import { Maybe } from 'maybe-not';
import { createGuid  } from '../utility/helpers';
import { interval } from 'rxjs';
import { filter, take } from 'rxjs/operators';

export function generateCustomElementWrapper<Props>(
  name: string,
  engine: DrySuiteEngine,
  options: {
    useShadowDOM: boolean;
    props?: ObservedProperty[];
    css?: string;
    prototypeExtensions?: Dict<any>;
  } = {
    useShadowDOM: true,
  }
): DSElementConstructor {
  const getShadowBaseElementM: Maybe<
    (element: DSCustomElement) => HTMLElement
  > = options.useShadowDOM
    ? Maybe.just(setupShadowDOM(name, options.css))
    : Maybe.nothing();
  const propsSetup = Array.isArray(options.props)
    ? Maybe.just(enableProps(options.props))
    : Maybe.nothing<{ fnHashByName: Dict<any>; hooks: DSElementSetupHooks }>();

  const ConstructF = function(): DSCustomElement {
    const self: DSCustomElement = Reflect.construct(
      HTMLElement,
      [],
      ConstructF
    );

    if (options.prototypeExtensions) {
        Object.keys(options.prototypeExtensions).forEach((key) => {
            if (!self[key]) {
                 self[key] = options.prototypeExtensions![key];
            }
        });
    }

    self.__onTeardown = [];
    self.__onAttached = [];
    self.__guid = createGuid();

    // if properties are enabled it sets __innerProps$
    propsSetup.map(({ hooks: { init } }) => init(self));

    // if we are using shadow dom go ahead an initialize app via engine
    // hand the engine streams that is populated with attributes once element is connected

    getShadowBaseElementM.map(fn => fn(self)).map(root => {
      const engineStreams = Maybe.maybe(self.__innerProps$)
        .map(props$ => engine(self, props$))
        .withDefaultFn(() => engine(self));

      handleEngineStreams(self, root, engineStreams);

      self.__shadowAppBooted = true;
    });

    return self;
  };

  tryAssigningNameProperties(ConstructF);

  //setup prototype methods
  ConstructF.prototype = Object.create(HTMLElement.prototype, {
    component: {
      writable: true,
      enumerable: true,
    },
    constructor: { value: ConstructF },
    connectedCallback: {
      writable: true,
      value: function(this: DSCustomElement): void {
        this.dataset.guid = `${this.__guid}`;
        if (!options.useShadowDOM || !this.__shadowAppBooted) {
          getShadowBaseElementM
            .map(fn => fn(this))
            .alt(Maybe.just(this))
            .map(root => {
              const engineStreams = Maybe.maybe(this.__innerProps$)
                .map(props$ => engine(this, props$))
                .withDefaultFn(() => engine(this));

              handleEngineStreams(this, root, engineStreams);
            });

          this.__shadowAppBooted = true;
        }

        propsSetup.map(({ hooks: { connectedCallback } }) => {
          connectedCallback(this);
        });

        this.__attached = true;
        this.__onAttached.forEach(fn => fn.call(this));
      }
    },
    disconnectedCallback: {
      writable: true,
      value: function(this: DSCustomElement): void {
        this.__onTeardown.reverse().forEach(fn => fn.call(this));
        this.__onTeardown = [];
        this.__onAttached = [];
        this.__attached = false;
        this.__shadowAppBooted = false;
      },
    },
    attributeChangedCallback: {
      writable: true,
      value: function(
        this: DSCustomElement,
        attrName: string,
        oldVal: any,
        newVal: any
      ): void {
        if (oldVal === newVal || !this.__attached) {
          return;
        }

        /* All observed attributes are Drysuite properties
           it sets the property which triggers the proxy property behavior subject */
        setPropFromAttr(
          this,
          attrName,
          newVal,
          propsSetup.map(propsSetup => propsSetup.fnHashByName)
        );
      },
  }
});

  // NEEDS TO HAPPEN AFTER OBJECT.CREATE SETS PROTOTYPE
  propsSetup.map(({ hooks: { classSetup } }) => classSetup(ConstructF));

  return ConstructF as DSElementConstructor;
}

export function createSetupElement<T>(
  elementConstructor: DSElementFactory<T>
): (elementOptions: T) => (elementName: string) => void {
  return (elementOptions): ((elementName: string) => void) => {
    return (elementName: string) => {
        const defineElement = () => {
            window.customElements.get(elementName) ||
            window.customElements.define(
              elementName,
              elementConstructor(elementName, elementOptions)
            );
        }

        if (!!window.customElements) {
           defineElement();
           return;
        }

        const whenCustomElementsReady = interval(5)
            .pipe(
                filter(() => !!window.customElements),
                take(1)
            )
            .toPromise();

            whenCustomElementsReady.then(() => {
                defineElement();
        });
    };
  };
}

function handleEngineStreams(
  element: DSCustomElement,
  root: HTMLElement,
  engineStreams: EngineStreams
): void {
  const placeholder = document.createElement('div');

  // this will be replaced
  root.appendChild(placeholder);

  const domSubscription = engineStreams.DOM$.subscribe(newElement => {
    root.replaceChild(newElement, root.firstChild!);
  });

  const settledSubscription = engineStreams.settled$.subscribe(settled => {
    element.settled = settled;

    if (settled) {
      element.dispatchEvent(new CustomEvent('settled'));
    }
  });

  const teardownSubscription = engineStreams.teardown$.subscribe(teardownFn => {
    element.__onTeardown.push(teardownFn);
  });

  element.__onTeardown.push(() => {
    [domSubscription, settledSubscription, teardownSubscription].forEach(s =>
      s.unsubscribe()
    );
  });
}

function tryAssigningNameProperties(ConstructF: () => DSCustomElement) {
  try {
    // Assign name to constructor if possible for semantics/helpfulness but swallow error if it doesn't allow (safari cough)
    Object.defineProperty(ConstructF, 'is', { value: name, writable: false });
    Object.defineProperty(ConstructF, 'localName', {
      value: name,
      writable: false,
    });
  } catch (error) {}
}
